import {
  Users, Wallet, Landmark, ShoppingCart, Store, GitBranch,
  BarChart3, UserCircle, ShieldCheck, Building2, Activity, Workflow,
  FileText, Boxes, GraduationCap, Plane, ClipboardCheck, Bell,
  Briefcase, Factory, LifeBuoy, TrendingUp, Headphones, Receipt, Zap,
  type LucideIcon,
} from "lucide-react";

export interface NavItem { label: string; href: string; }

export const NAV_ITEMS: NavItem[] = [
  { label: "Product", href: "/features" },
  { label: "Pricing", href: "/pricing" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
}

/** Grouped by business area for the homepage module section — mirrors real RBAC modules. */
export interface ModuleGroup {
  category: string;
  blurb: string;
  items: Feature[];
}

export const MODULE_GROUPS: ModuleGroup[] = [
  {
    category: "People",
    blurb: "Hire, pay and support your team.",
    items: [
      { icon: Users, title: "HR & Employee Lifecycle", description: "Employee records, org structure, departments and designations in one place." },
      { icon: Wallet, title: "Payroll", description: "Automated salary runs with India-ready PF, ESI, Professional Tax and TDS." },
      { icon: UserCircle, title: "Employee Self-Service", description: "Staff manage attendance, leave, payslips and profiles independently." },
      { icon: Briefcase, title: "Recruitment & ATS", description: "Job postings, candidate pipelines, interviews and offer letters." },
    ],
  },
  {
    category: "Finance",
    blurb: "Keep books accurate and spend visible.",
    items: [
      { icon: Landmark, title: "Finance & Accounting", description: "Ledgers, GST-ready invoicing, fiscal years and real-time financial visibility." },
      { icon: Plane, title: "Travel & Expense", description: "Travel requests, expense claims and reimbursements with approval routing." },
      { icon: BarChart3, title: "Financial Reporting", description: "Exportable MIS reports and live dashboards scoped to each role." },
    ],
  },
  {
    category: "Operations",
    blurb: "Run procurement, stock and production.",
    items: [
      { icon: ShoppingCart, title: "Procurement", description: "Purchase requisitions, orders and approvals with full spend control." },
      { icon: Boxes, title: "Inventory & Warehouse", description: "Multi-location stock, batches and reorder tracking." },
      { icon: Store, title: "Vendor Portal", description: "A dedicated, secure space for vendors to track POs, bills and payments." },
      { icon: Factory, title: "Manufacturing & Quality", description: "Production orders, bills of material and quality inspection (NCR/CAPA)." },
      { icon: FileText, title: "Document Management", description: "Centralized storage for contracts, policies and business records." },
    ],
  },
  {
    category: "Customer",
    blurb: "Manage pipeline and support in one system.",
    items: [
      { icon: TrendingUp, title: "CRM & Sales", description: "Lead pipeline, quotations and customer accounts." },
      { icon: LifeBuoy, title: "Helpdesk", description: "IT and HR ticketing with status tracking." },
      { icon: Headphones, title: "Customer Service", description: "Service requests and field operations tracking." },
    ],
  },
];

/** Total count derived from MODULE_GROUPS — never hardcode this number elsewhere. */
export const TOTAL_MODULE_COUNT = MODULE_GROUPS.reduce((sum, g) => sum + g.items.length, 0);

export interface Benefit {
  icon: LucideIcon;
  title: string;
  description: string;
}

/** Business-outcome framing of platform capabilities — used in the "Enterprise controls" section. */
export const BENEFITS: Benefit[] = [
  { icon: Building2, title: "One Source of Truth", description: "HR, finance, payroll, procurement and operations work from the same connected data." },
  { icon: Workflow, title: "Automated Approvals", description: "Move requests through configurable approval workflows without chasing people." },
  { icon: Activity, title: "Real-Time Visibility", description: "Monitor business activity without waiting for manual spreadsheet updates." },
  { icon: ShieldCheck, title: "Secure Access", description: "Role-based permissions help keep sensitive business data controlled." },
  { icon: Boxes, title: "Isolated by Design", description: "Each company operates in its own secure, multi-tenant workspace." },
  { icon: FileText, title: "Full Audit Trail", description: "Every meaningful action is logged with actor, timestamp and before/after state." },
];

export interface WorkflowStep { label: string; }
export const WORKFLOW_STEPS: WorkflowStep[] = [
  { label: "Employee Request" },
  { label: "Manager Approval" },
  { label: "HR Review" },
  { label: "Finance Approval" },
  { label: "Payment / Processed" },
  { label: "Dashboard Updated" },
];

/** Verified, honest product facts — replaces unverifiable customer/uptime numbers. */
export interface ProductFact { icon: LucideIcon; label: string; }
export const PRODUCT_FACTS: ProductFact[] = [
  { icon: Boxes, label: `${TOTAL_MODULE_COUNT}+ integrated business modules` },
  { icon: Landmark, label: "Organization-based pricing, not per employee" },
  { icon: ShieldCheck, label: "Role-based access & multi-tenant isolation" },
  { icon: Workflow, label: "Configurable, multi-level approval workflows" },
];

/** Traditional disconnected tooling vs. Alea ERP — used in the "Why Alea" section. */
export const TRADITIONAL_SETUP: string[] = [
  "Multiple disconnected tools",
  "HR and payroll in separate systems",
  "Finance disconnected from operations",
  "Manual approval follow-ups over email",
  "Spreadsheet-based reporting",
  "A separate subscription for every tool",
];

export const ALEA_WAY: string[] = [
  "One integrated platform",
  "Connected business data across modules",
  "Configurable approval workflows",
  "Real-time reporting from live data",
  "One organization-based price",
  "One ERP instead of many disconnected systems",
];

/** Indian statutory capabilities — verified against payroll/finance modules. */
export interface ComplianceItem { icon: LucideIcon; label: string; }
export const INDIA_COMPLIANCE: ComplianceItem[] = [
  { icon: Wallet, label: "Provident Fund (PF)" },
  { icon: ShieldCheck, label: "ESI" },
  { icon: Receipt, label: "Professional Tax" },
  { icon: FileText, label: "TDS" },
  { icon: Landmark, label: "GST-ready invoicing" },
  { icon: ClipboardCheck, label: "Statutory payroll compliance" },
];

/** Genuinely supported use cases, grounded in real modules — replaces unverified testimonials. */
export interface UseCase { icon: LucideIcon; title: string; description: string; }
export const USE_CASES: UseCase[] = [
  { icon: Factory, title: "Manufacturing", description: "Production orders, inventory, procurement, quality and finance in one workflow." },
  { icon: Briefcase, title: "Professional Services", description: "Employees, expenses, approvals, finance and operations without disconnected tools." },
  { icon: Store, title: "Retail & Distribution", description: "Inventory, procurement, sales pipeline, CRM and finance working together." },
  { icon: Building2, title: "Growing Enterprises", description: "Multi-department operations, approval workflows, reporting and role-based access." },
];

/** Single feature/module entry for the Features page — business-outcome oriented. */
export interface FeatureModule {
  /** Stable id used for preview lookup and anchors. */
  id: string;
  icon: LucideIcon;
  name: string;
  /** Short business-oriented headline. */
  headline: string;
  description: string;
  /** 3–4 concrete, verified capabilities. */
  capabilities: string[];
}

export type FeatureCategoryLayout = "modules" | "paired";

export interface FeatureCategory {
  id: string;
  /** Compact label shown in the sticky module nav. */
  navLabel: string;
  title: string;
  intro: string;
  layout: FeatureCategoryLayout;
  modules: FeatureModule[];
}

/**
 * Features-page source of truth: modules grouped for conceptual clarity.
 * Preview components are keyed by `module.id` in Features.tsx.
 */
export const FEATURE_CATEGORIES: FeatureCategory[] = [
  {
    id: "hr-people",
    navLabel: "HR & Payroll",
    title: "HR & People",
    intro:
      "Hire, pay and support your workforce from one connected people stack — records, payroll and self-service share the same employee data.",
    layout: "modules",
    modules: [
      {
        id: "HRMS",
        icon: Users,
        name: "HRMS",
        headline: "Manage your entire employee lifecycle from one place.",
        description:
          "Centralize employee records, organizational structure and lifecycle processes so HR, managers and payroll work from the same truth.",
        capabilities: [
          "Employee lifecycle management",
          "Departments and designations",
          "Attendance and leave",
          "Centralized employee documentation",
        ],
      },
      {
        id: "Payroll",
        icon: Wallet,
        name: "Payroll",
        headline: "Run accurate, India-ready payroll without spreadsheet chaos.",
        description:
          "Process salary runs with statutory deductions built into the workflow — then deliver payslips through employee self-service.",
        capabilities: [
          "Automated salary processing",
          "PF, ESI, Professional Tax and TDS",
          "Payslip generation",
          "Earnings and deductions control",
        ],
      },
      {
        id: "Employee Self-Service",
        icon: UserCircle,
        name: "Employee Self-Service",
        headline: "Give employees a portal for day-to-day requests.",
        description:
          "Staff manage attendance, leave, payslips and profiles themselves — reducing HR follow-ups and speeding approvals.",
        capabilities: [
          "My attendance",
          "My leave requests",
          "My payslips",
          "Profile and personal details",
        ],
      },
      {
        id: "Learning & Travel",
        icon: GraduationCap,
        name: "Learning & Travel",
        headline: "Extend people operations into learning and travel expenses.",
        description:
          "Track learning activity and travel/expense claims with the same approval patterns used across the platform.",
        capabilities: [
          "L&D tracking",
          "Travel requests",
          "Expense claims",
          "Reimbursement workflows",
        ],
      },
    ],
  },
  {
    id: "finance-ops",
    navLabel: "Finance & Ops",
    title: "Finance & Operations",
    intro:
      "Keep financial operations connected to procurement and vendors — spend, invoices and payments stay visible across teams.",
    layout: "modules",
    modules: [
      {
        id: "Finance",
        icon: Landmark,
        name: "Finance",
        headline: "Keep financial operations connected to the rest of your business.",
        description:
          "Manage ledgers, invoices and GST-ready billing with visibility that reflects activity from other modules — not a disconnected accounting silo.",
        capabilities: [
          "Invoices and ledgers",
          "GST-ready invoicing",
          "Fiscal year management",
          "Financial reporting views",
        ],
      },
      {
        id: "Procurement",
        icon: ShoppingCart,
        name: "Procurement",
        headline: "Control spend from requisition through purchase order.",
        description:
          "Route purchase requests through approvals, issue POs and keep vendor linkage intact so finance can see what operations committed to buy.",
        capabilities: [
          "Purchase requisitions",
          "Purchase orders",
          "Approval routing",
          "Vendor linkage",
        ],
      },
      {
        id: "Vendor Portal",
        icon: Store,
        name: "Vendor Portal",
        headline: "Give suppliers a secure window into POs, bills and payments.",
        description:
          "Vendors track purchase orders, submit bills and follow payment status without email chains — while your team keeps control inside Alea.",
        capabilities: [
          "PO visibility for vendors",
          "Bill submission",
          "Payment status tracking",
          "Vendor profile management",
        ],
      },
    ],
  },
  {
    id: "workflows",
    navLabel: "Workflows",
    title: "Workflow & Control",
    intro:
      "Move work between departments with configurable approvals, then control access and keep an auditable record of what changed.",
    layout: "modules",
    modules: [
      {
        id: "Approval Engine",
        icon: GitBranch,
        name: "Approval Engine",
        headline: "Automate the work between departments.",
        description:
          "Move requests through configurable, multi-level approval chains — status tracked, decisions recorded, no chasing people over email.",
        capabilities: [
          "Multi-level approval chains",
          "Conditional routing",
          "Delegations",
          "Status tracking",
        ],
      },
      {
        id: "RBAC",
        icon: ShieldCheck,
        name: "Role-Based Access",
        headline: "Control what different roles can see and do.",
        description:
          "Map permissions to your org structure so people get the modules and actions they need — and nothing they should not.",
        capabilities: [
          "Custom roles",
          "Module-level permissions",
          "Department access matrices",
          "Self-service permission tier",
        ],
      },
      {
        id: "Audit Logs",
        icon: FileText,
        name: "Audit Logs",
        headline: "Track important actions and changes across the system.",
        description:
          "Every meaningful action is logged with actor, timestamp and before/after context — ready when leadership or compliance asks what happened.",
        capabilities: [
          "Action logging",
          "Actor and timestamp",
          "Before / after state",
          "Searchable audit trail",
        ],
      },
    ],
  },
  {
    id: "analytics",
    navLabel: "Analytics",
    title: "Analytics & Reporting",
    intro:
      "Turn operational data into decisions — live dashboards for visibility, detailed reports when leadership needs the numbers on record.",
    layout: "paired",
    modules: [
      {
        id: "Analytics",
        icon: BarChart3,
        name: "Analytics",
        headline: "See business activity as it happens.",
        description:
          "Role-aware dashboards and KPIs surface operational trends without waiting for someone to rebuild a spreadsheet.",
        capabilities: [
          "Live dashboards",
          "Module KPIs",
          "Drill-down charts",
          "Trend analysis",
        ],
      },
      {
        id: "Reports",
        icon: BarChart3,
        name: "Reports",
        headline: "Export the MIS reports leadership needs, on demand.",
        description:
          "Generate detailed operational and financial reports from the same live data — filtered, role-scoped and exportable.",
        capabilities: [
          "MIS and operational reports",
          "Excel / PDF export",
          "Custom filters",
          "Role-scoped data",
        ],
      },
    ],
  },
];

/** Flat list for any consumer that needs all feature modules. */
export const DETAILED_MODULES: FeatureModule[] = FEATURE_CATEGORIES.flatMap((c) => c.modules);

/** Sticky nav items derived from categories — never hardcode separately. */
export const FEATURE_NAV = FEATURE_CATEGORIES.map((c) => ({
  id: c.id,
  label: c.navLabel,
}));

/** Conceptual integration diagram — business functions that share platform data. */
export const INTEGRATION_HUB = [
  "HR",
  "Payroll",
  "Finance",
  "Procurement",
  "Inventory",
  "CRM",
  "Operations",
] as const;

/** Procurement cross-module flow supported by requisitions → approvals → POs → vendors → finance. */
export const PROCUREMENT_FLOW: WorkflowStep[] = [
  { label: "Requisition" },
  { label: "Approval" },
  { label: "Purchase Order" },
  { label: "Vendor" },
  { label: "Receipt" },
  { label: "Finance" },
];

/** Approval chain — aligned with the platform approval engine roles. */
export const APPROVAL_FLOW: WorkflowStep[] = [
  { label: "Employee" },
  { label: "Manager" },
  { label: "HR" },
  { label: "Finance" },
  { label: "Completed" },
];

export const COMPANY = {
  name: "Alea ERP",
  email: "ash@aleaitsolutions.com",
  phone: "+91 96103 65064",
  address: "C, 155, Sunder Marg, Tilak Nagar, Jaipur, Rajasthan 302004",
  /** City/region derived from verified contact address — not an invented HQ claim. */
  location: "Jaipur, Rajasthan",
};

/** Contact form qualification options — must stay aligned with API `/contact` schema. */
export const CONTACT_COMPANY_SIZES = [
  { value: "1-25", label: "1–25" },
  { value: "26-100", label: "26–100" },
  { value: "101-500", label: "101–500" },
  { value: "501-1000", label: "501–1,000" },
  { value: "1000+", label: "1,000+" },
] as const;

export const CONTACT_INTERESTS = [
  "HR & Employee Management",
  "Payroll",
  "Finance & Accounting",
  "Procurement",
  "Vendor Management",
  "Inventory",
  "Reporting & Analytics",
  "Complete ERP",
  "Other",
] as const;

/** Compact module trust strip for Contact — verified modules only. */
export const CONTACT_TRUST_MODULES = [
  "HR",
  "Payroll",
  "Finance",
  "Procurement",
  "Workflows",
  "Reporting",
] as const;

/** About-page company snapshot — only verified product/company facts. */
export const COMPANY_SNAPSHOT: { label: string; value: string }[] = [
  { label: "Based in", value: "Jaipur, Rajasthan" },
  { label: "Integrated modules", value: `${TOTAL_MODULE_COUNT}+` },
  { label: "Pricing model", value: "Per organization" },
  { label: "Indian payroll", value: "PF · ESI · PT · TDS" },
];

/** Problem → Alea solution pairs for the About page. */
export const ABOUT_PROBLEMS: { problem: string; solution: string }[] = [
  {
    problem: "Data scattered across spreadsheets and siloed apps",
    solution: "One connected ERP with shared business data across modules",
  },
  {
    problem: "Approvals lost in email threads with no accountability",
    solution: "Configurable, multi-level approval workflows with status tracking",
  },
  {
    problem: "Payroll and compliance handled manually, error by error",
    solution: "Integrated payroll with Indian statutory workflows (PF, ESI, PT, TDS)",
  },
  {
    problem: "No single, trustworthy view of the business",
    solution: "Centralized analytics, MIS reports and role-aware dashboards",
  },
];

/** Product differentiators — verified capabilities only, no competitive superlatives. */
export const ABOUT_DIFFERENTIATORS: {
  icon: LucideIcon;
  title: string;
  description: string;
}[] = [
  {
    icon: Building2,
    title: "One Connected Platform",
    description:
      "HR, payroll, finance, procurement and operations work from connected business data — not a pile of disconnected tools.",
  },
  {
    icon: Workflow,
    title: "Built Around Workflows",
    description:
      "Move requests and approvals through the organization without relying on manual follow-ups over email.",
  },
  {
    icon: Landmark,
    title: "Predictable Organization-Based Pricing",
    description:
      "Choose a plan based on your organization size and user capacity — not a separate fee for every employee.",
  },
  {
    icon: ClipboardCheck,
    title: "Built for Real Business Operations",
    description:
      "Designed around the workflows teams actually use: employee requests, payroll runs, procurement and finance.",
  },
];

/** Product-specific principles — replaces generic SaaS value copy. */
export const ABOUT_PRINCIPLES: {
  icon: LucideIcon;
  title: string;
  description: string;
}[] = [
  {
    icon: ShieldCheck,
    title: "Trust by Design",
    description:
      "Business software handles sensitive information. Access control, audit trails and accountability should be built into the product.",
  },
  {
    icon: Zap,
    title: "Simplicity Over Complexity",
    description:
      "ERP software does not need to be difficult just because the business is complex. We fight unnecessary bloat.",
  },
  {
    icon: Boxes,
    title: "Connected by Default",
    description:
      "Business functions should share data instead of becoming another isolated system your team has to reconcile.",
  },
  {
    icon: Users,
    title: "Built for Real Operations",
    description:
      "We optimize for the work teams actually do — approvals, payroll, procurement and reporting — not just what looks good in a demo.",
  },
];

/**
 * Product development journey — NOT company history.
 * Used because founding dates / customer milestones could not be verified.
 */
export const PRODUCT_JOURNEY: { step: string; title: string; text: string }[] = [
  {
    step: "01",
    title: "Start with connected business data",
    text: "Bring core people, payroll and finance functions into one platform so teams stop reconciling siloed tools.",
  },
  {
    step: "02",
    title: "Build around workflows",
    text: "Connect approvals and operational processes so requests move from employee to manager to finance with a clear trail.",
  },
  {
    step: "03",
    title: "Expand business visibility",
    text: "Bring analytics, MIS reports and module KPIs into the same system — decisions from live data, not yesterday's spreadsheets.",
  },
];

/** Journey links connecting About → Product → Pricing → Demo. */
export const ABOUT_JOURNEY_LINKS: { label: string; href: string; blurb: string }[] = [
  { label: "Product", href: "/features", blurb: "See what Alea can do." },
  { label: "Pricing", href: "/pricing", blurb: "See how Alea is priced." },
  { label: "Demo", href: "/demo-access", blurb: "See Alea in action." },
  { label: "Contact", href: "/contact", blurb: "Talk to our team." },
];

export const PLANS = [
  {
    name: "Starter",
    price: "₹0",
    cadence: "for 14 days",
    tagline: "Everything you need to evaluate Alea ERP.",
    cta: "Start Free Trial",
    highlighted: false,
    features: ["Up to 25 employees", "HR & Self-Service", "Attendance & Leave", "Email support", "1 company tenant"],
  },
  {
    name: "Growth",
    price: "₹199",
    cadence: "per employee / month",
    tagline: "For growing teams that need full operations control.",
    cta: "Start Free Trial",
    highlighted: true,
    features: ["Unlimited employees", "Payroll & Finance", "Procurement & Vendor Portal", "Approval workflows", "Analytics & reports", "Priority support"],
  },
  {
    name: "Enterprise",
    price: "Custom",
    cadence: "tailored to you",
    tagline: "Advanced security, scale and dedicated success.",
    cta: "Book Demo",
    highlighted: false,
    features: ["Everything in Growth", "Multi-tenant management", "Custom RBAC & audit", "SSO & advanced security", "Dedicated success manager", "SLA-backed uptime"],
  },
];

export const NOTIFY_ICON = Bell;
export const PLANE_ICON = Plane;
